<template>
  <div>
    <v-alert
      v-model="popupVisible"
      v-touch="{
        up: () => popupVisible = false,
        right: () => popupVisible = false,
        left: () => popupVisible = false
      }"
      border="left"
      color="primary"
      outlined
      colored-border
      dense
      elevation="3"
      transition="slide-y-transition"
      class="pa-0"
    >
      <div v-if="popupNotification" class="d-flex align-stretch">
        <div
          class="popup-notification-content flex d-flex align-center text-truncate pa-2"
          :class="{'pl-3': small}"
          @click="_show(popupNotification); popupVisible = false"
        >
          <v-avatar v-if="!small" color="primary" size="36" class="mr-2">
            <v-icon small class="white--text">{{_notificationIcon(popupNotification)}}</v-icon>
          </v-avatar>
          <div class="flex text-truncate">
            <Notification :notification="popupNotification" secondary />
            <small class="grey--text">{{_deviceName(popupNotification)}}</small>
          </div>
        </div>
        <v-btn
          v-if="small"
          icon
          small
          class="h-100"
          @click="_show(popupNotification); popupVisible = false"
        >
          <v-icon small>far fa-chevron-right</v-icon>
        </v-btn>
        <template v-else>
          <v-divider vertical class="px-1"></v-divider>
          <Notification
            :notification="popupNotification"
            actions-secondary
            class="d-flex flex-column" />
        </template>
      </div>
    </v-alert>

    <v-navigation-drawer
      ref="drawer"
      v-model="sidebarOpen"
      width="400"
      app
      absolute
      temporary
      stateless
      right
    >
      <template #prepend>
        <v-list-item two-line>
          <v-list-item-avatar class="my-0 mr-1">
            <v-icon dense>far fa-bell-on</v-icon>
          </v-list-item-avatar>

          <v-list-item-content>
            <v-list-item-title>Benachrichtigungen</v-list-item-title>
          </v-list-item-content>

          <v-btn icon @click="_back">
            <v-icon>fal fa-times</v-icon>
          </v-btn>
        </v-list-item>

        <v-divider></v-divider>
      </template>

      <section class="d-flex flex-column py-4 px-3" @keydown.esc="_back">
        <div v-if="notifications.length">
          <div class="text-right">
            <v-btn
              text
              small
              color="primary"
              :disabled="!notifications.length"
              @click="_markAllRead"
            >Alles gelesen</v-btn>
          </div>
          <v-list dense class="py-0">
            <NotificationItem
              v-for="notification in notifications"
              :key="notification.id"
              :notification="notification"
              :value="routeActive && $route.query.notification === notification.id"
              :visible="sidebarOpen" />
          </v-list>
        </div>

        <div v-else class="flex d-flex flex-column justify-center text-center">
          <div>
            <v-icon x-large color="secondary">fad fa-bell-on</v-icon><br>
            <div class="mt-3">Keine Benachrichtigungen</div>
          </div>
        </div>
      </section>

      <template #append>
        <v-divider class="my-4"></v-divider>

        <v-list-item>
          <v-list-item-content class="pt-0">
            <label class="d-flex align-center justify-space-between mb-4">
              <div class="d-flex align-center">
                <v-icon dense class="mr-2">far fa-cog</v-icon>
                <span class="font-weight-500">Einstellungen</span>
              </div>

              <v-btn v-if="pref_loading" color="primary" loading icon x-small></v-btn>
            </label>

            <div class="d-flex">
              <small class="flex">
                E-Mail Benachrichtigungen aktivieren<br>
                <small v-if="pref_notifications">
                  Statusmeldungen per E-Mail aktiviert
                </small>
                <small v-if="!pref_notifications">
                  Statusmeldungen per E-Mail deaktiviert
                </small>
              </small>
              <v-switch
                :input-value="pref_notifications"
                :disabled="pref_loading"
                color="primary"
                @click="_togglePref_notifications"
              ></v-switch>
            </div>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-navigation-drawer>
  </div>
</template>

<script>
  import RouteDialogMixin from './dialogs/RouteDialogMixin';
  import Notification from './notifications/Notification.vue';
  import NotificationItem from './notifications/NotificationItem.vue';
  import {show, markAllRead} from './notifications/notification-utils';
  import {getDeviceName} from './peers/peer-utils';
  import {icon as notificationIcon} from './notifications/notification-utils';
  import eventBus from '../assets/js/event-bus';
  import {routeOptions} from '../assets/js/utils';


  export default {
    name: 'Notifications',

    components: {
      Notification,
      NotificationItem
    },

    mixins: [
      RouteDialogMixin
    ],

    data() {
      return {
        popupVisible: false,
        popupNotification: null,
        sidebarOpen: false,
        pref_loading: false,
        // RouteDialogMixin
        routePath: '/share/notifications'
      };
    },

    computed: {
      user() {
        return this.$store.getters['session/getUser'];
      },

      pref_notifications() {
        const preferences = this.user?.preferences;
        if (preferences) {
          return preferences.notifications === false ? false : true;
        }
        return false;
      },

      notifications() {
        return this.$store.getters['share/getNotifications'];
      },

      small() {
        return this.$vuetify.breakpoint.width < 400;
      }
    },

    watch: {
      sidebarOpen(sidebarOpen) {
        // Close popup if sidebar opens.
        if (sidebarOpen && this.popupVisible) {
          this.popupVisible = false;
        }

        if (sidebarOpen) {
          // Go back / close drawer if clicked outside.
          this.$nextTick(() => {
            const drawerParent = this.$refs.drawer.$el.parentNode;
            const overlay = [...drawerParent.childNodes].find((child) => child.matches('.v-overlay'));
            overlay.addEventListener('click', () => this._back());
          });
        }
      }
    },

    mounted() {
      this._unsubscribe = this.$store.subscribe(async ({type, payload}) => {
        if (this.sidebarOpen) {
          return;
        }

        if (type === 'share/addNotification') {
          clearTimeout(this._popupNotification_timeout);

          // Close popup if there is already a notification visible.
          if (this.popupVisible) {
            this.popupVisible = false;
            await new Promise((resolve) => setTimeout(resolve, 450));
          }
          this.popupNotification = payload;
          this.popupVisible = true;

        } else if (type === 'share/updateNotification' && this.popupNotification?.id === payload.id) {
          this.popupNotification = payload;

        } else if (type === 'share/removeNotification' && this.popupNotification?.id === payload) {
          this.popupVisible = false;
          this._popupNotification_timeout = setTimeout(() => this.popupNotification = null, 450);
        }
      });
    },

    destroyed() {
      this._unsubscribe?.();
    },

    methods: {
      show() {
        this.sidebarOpen = true;
      },

      hide() {
        this.sidebarOpen = false;
      },

      toggle() {
        this.sidebarOpen = !this.sidebarOpen;
      },

      _notificationIcon(notification) {
        return notificationIcon(notification);
      },

      _deviceName(notification) {
        const peer = notification.data?.peer;
        return peer ? getDeviceName(peer) : '';
      },

      _back() {
        this.$router.replace(routeOptions({path: '/share'}));
      },

      _show(notification) {
        show(notification);
      },

      _markAllRead() {
        markAllRead(this.notifications);
      },

      async _setPreferences(preferences) {
        if (this.pref_loading) {
          return;
        }

        this.pref_loading = true;
        try {
          await this.$api.post('UserSetPreferences', preferences);
        } catch (error) {
          eventBus.$emit('notifyUser', 'Einstellungen könnten nicht gespeichert werden');
        }
        this.$auth.verifyUser(); // Updates the user.
        this.pref_loading = false;
      },

      _togglePref_notifications() {
        clearTimeout(this._togglePref_notificationsTimeout);
        this._togglePref_notificationsTimeout = setTimeout(() => {
          this._setPreferences({notifications: !this.pref_notifications});
        }, 10);
      }
    }
  };
</script>

<style lang="scss" scoped>
  .v-alert {
    position: fixed;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;
    background-color: #fff !important;
    width: calc(100% - 20px);
    max-width: 450px;

    &::v-deep {
      .v-alert__border {
        border-radius: 0;
      }

      .v-alert__icon {
        margin: auto 0;
      }

      .v-alert__dismissible {
        margin-left: 0;
      }

      .v-alert__content {
        overflow: hidden;
      }
    }
  }

  .popup-notification-content {
    cursor: pointer;
  }

  .notification::v-deep button {
    flex: 1 1 auto;
    border-radius: 0;
  }

  button.h-100 {
    height: auto !important;
  }

  section {
    height: 100%;
  }

  .v-list {
    margin-left: -12px;
    margin-right: -12px;
  }

  .v-input--switch {
    margin-top: 0;
    padding-top: 0;
  }

  .v-input--switch::v-deep .v-input__slot {
    margin-bottom: 0;
  }

  .v-input--switch::v-deep .v-messages {
    display: none;
  }
</style>
